import React from 'react';
import { EpThreatAnalysisResult, AnalysisResultDisplayProps } from '@types';
import { RISK_LEVEL_COLORS } from '../constants';
import UnifiedMap from '../UnifiedMap';
import useGoogleMaps from '../useGoogleMaps';
import { Section } from './ReportShared';
import { getMapCoordinates } from '../../utils/mapCoordinates';

interface EpThreatAnalysisContentProps {
    result: EpThreatAnalysisResult;
    onConvert?: AnalysisResultDisplayProps['onConvertAnalysisToProject'];
}

const EpThreatAnalysisContent: React.FC<EpThreatAnalysisContentProps> = ({ result, onConvert }) => {
    const { isLoaded } = useGoogleMaps();
    const riskColor = RISK_LEVEL_COLORS[result.threatLevel] || RISK_LEVEL_COLORS['Medium'];
    
    const markers = (result.mapMarkers || []).filter(m => getMapCoordinates(m)).map(m => ({
        ...m,
        id: m.name,
        ...getMapCoordinates(m)!,
        name: m.name,
        color: m.type === 'Threat' ? '#EF4444' : m.type === 'Evac Point' ? '#2563EB' : m.type === 'Medical' ? '#10B981' : '#7C3AED',
        type: m.type,
        description: m.description
    }));
    
    const mapCenter = markers.length > 0 ? { lat: markers[0].lat, lng: markers[0].lng } : undefined;

    return (
        <div className="space-y-8">
            <div className="flex items-start gap-6 bg-slate-50 p-6 rounded-xl border border-slate-200">
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-bold text-slate-800 text-sm uppercase">Protective Intelligence Summary</h3>
                        {onConvert && (
                            <button 
                                onClick={() => onConvert(result)}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-1.5 px-3 rounded shadow-sm transition-colors flex items-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                Initialize Project from Analysis
                            </button>
                        )}
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{result.executiveSummary}</p>
                </div>
                <div className="flex flex-col items-end gap-2 min-w-[150px]">
                    <div className={`px-4 py-2 rounded text-sm font-bold uppercase border text-center w-full ${riskColor.bg} ${riskColor.text} ${riskColor.border}`}>
                        Threat Level: {result.threatLevel}
                    </div>
                    <div className="text-xs text-slate-500 text-right">
                        <strong>Principal:</strong><br/>
                        {result.principalName}<br/>
                    </div>
                </div>
            </div>

            {result.mapMarkers && result.mapMarkers.length > 0 && (
                <Section title="Geospatial Threat & Extraction Map">
                     <div className="w-full h-96 rounded-lg overflow-hidden border border-slate-300 relative shadow-sm">
                     <UnifiedMap
                        mapTitle="Geospatial Threat & Extraction Map"
                        isLoaded={isLoaded}
                        choroplethEnabled={false}
                        initialViewState={{ 
                            longitude: mapCenter?.lng || 0, 
                            latitude: mapCenter?.lat || 0, 
                            zoom: 12
                        }}
                        theme="light"
                        markers={markers || []}
                        riskData={[]}
                    />
                    </div>
                </Section>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Section title="Direct Threat Actors">
                    <div className="space-y-3">
                        {result.directThreats.map((t, i) => (
                            <div key={i} className="bg-red-50 p-4 rounded border border-red-100">
                                <h5 className="font-bold text-red-800 text-sm mb-1">{t.actor}</h5>
                                <p className="text-xs text-red-700 mb-2">{t.intent}</p>
                                <span className="text-[10px] bg-white px-2 py-0.5 rounded border border-red-200 text-red-600 font-bold">CAPABILITY: {t.capability}</span>
                            </div>
                        ))}
                        {result.directThreats.length === 0 && <p className="text-sm text-slate-500 italic">No direct threats identified.</p>}
                    </div>
                </Section>

                {result.riskMatrix && (
                    <Section title="Threat Risk Matrix">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-left">
                                <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
                                    <tr>
                                        <th className="px-3 py-2">Threat</th>
                                        <th className="px-3 py-2">Likelihood</th>
                                        <th className="px-3 py-2">Impact</th>
                                        <th className="px-3 py-2">Score</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {result.riskMatrix.map((row, i) => (
                                        <tr key={i}>
                                            <td className="px-3 py-2 font-medium">{row.threat}</td>
                                            <td className="px-3 py-2">{row.likelihood}</td>
                                            <td className="px-3 py-2">{row.impact}</td>
                                            <td className="px-3 py-2 font-bold">{row.score}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                )}
            </div>

            {result.extractionLogistics && (
                <Section title="Emergency Extraction & Logistics">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                         <div className="p-3 bg-blue-50 rounded border border-blue-100">
                             <h5 className="font-bold text-blue-800 mb-1">Nearest Airport (Evac)</h5>
                             <p className="text-blue-900">{result.extractionLogistics.nearestAirport}</p>
                         </div>
                         <div className="p-3 bg-green-50 rounded border border-green-100">
                             <h5 className="font-bold text-green-800 mb-1">Primary Land Border</h5>
                             <p className="text-green-900">{result.extractionLogistics.landBorders}</p>
                         </div>
                         <div className="p-3 bg-amber-50 rounded border border-amber-100">
                             <h5 className="font-bold text-amber-800 mb-1">Visa & Entry Requirements</h5>
                             <p className="text-amber-900">{result.extractionLogistics.visaRequirements}</p>
                         </div>
                         <div className="p-3 bg-slate-50 rounded border border-slate-200">
                             <h5 className="font-bold text-slate-800 mb-1">Embassy Contact</h5>
                             <p className="text-slate-900">{result.extractionLogistics.embassyContact}</p>
                         </div>
                    </div>
                </Section>
            )}

            <Section title="Protective Recommendations">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {result.recommendations.map((rec, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded border border-slate-100">
                             <div className="mt-0.5 text-blue-600 flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                             </div>
                             <p className="text-sm text-slate-700">{rec}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* --- Staff Protection: Communications Plan (only rendered when present) --- */}
            {(result as any).communicationsPlan && (
                <Section title="Communications Plan (ISO 31030)">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {[
                            { label: 'Primary Comms Method', key: 'primaryMethod', color: 'blue' },
                            { label: 'Backup Comms Method', key: 'backupMethod', color: 'slate' },
                            { label: 'Check-in Schedule', key: 'checkinSchedule', color: 'green' },
                            { label: 'Emergency / Crisis Line', key: 'emergencyLine', color: 'red' },
                            { label: 'Silence Protocol', key: 'silenceProtocol', color: 'amber' },
                        ].map(({ label, key, color }) => {
                            const val = (result as any).communicationsPlan[key];
                            if (!val) return null;
                            return (
                                <div key={key} className={`p-3 bg-${color}-50 rounded border border-${color}-100`}>
                                    <h5 className={`font-bold text-${color}-800 mb-1 text-xs uppercase`}>{label}</h5>
                                    <p className={`text-${color}-900 text-sm`}>{val}</p>
                                </div>
                            );
                        })}
                    </div>
                </Section>
            )}

            {/* --- Staff Protection: Duty of Care Checklist (only rendered when present) --- */}
            {(result as any).dutyOfCareChecklist && (result as any).dutyOfCareChecklist.length > 0 && (
                <Section title="Duty-of-Care Checklist (ISO 31030 Compliance)">
                    <div className="space-y-2">
                        {(result as any).dutyOfCareChecklist.map((item: string, i: number) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded">
                                <div className="flex-shrink-0 w-5 h-5 border-2 border-green-400 rounded bg-white flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <span className="text-sm text-green-900">{item}</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-green-700 mt-3 italic">
                        This checklist represents mandatory organizational duty-of-care obligations under ISO 31030 Travel Risk Management. 
                        All items should be completed and documented before deployment commences.
                    </p>
                </Section>
            )}
        </div>
    );
};

export default EpThreatAnalysisContent;
